import { AddOutlined } from '@mui/icons-material';
import { Box, ButtonBase, Menu, MenuItem, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ReactElement, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

export interface PaloAccountLogoProps {
    name: string;
    src?: string;
    /** No logo before the first save: there is nothing to attach it to yet. */
    disabled?: boolean;
    onAdd: () => void;
    onRemove: () => void;
}

/**
 * The logo of a member or a supplier in the head of its section.
 *
 * Company logos are mostly wide: half of the supplier logos are wider than
 * 2:1, and a circle left a stripe of a few pixels of them. They get a
 * rectangle in their own proportions instead. A click opens a menu, because
 * removing the logo straight from it was one stray click away (R-265).
 */
export const PaloAccountLogo = ({ name, src, disabled, onAdd, onRemove }: PaloAccountLogoProps): ReactElement => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    // A file that has gone missing shows the initials, not the browser's broken image.
    const [failed, setFailed] = useState(false);

    useEffect(() => setFailed(false), [src]);

    const box = (
        <ButtonBase
            disabled={disabled}
            aria-label={name}
            onClick={event => (src ? setAnchor(event.currentTarget) : onAdd())}
            sx={{
                width: 120,
                height: 48,
                padding: '4px',
                gap: '4px',
                borderRadius: '4px',
                border: `1px ${src ? 'solid' : 'dashed'} ${tokens.color.line}`,
                backgroundColor: tokens.color.surface,
                color: tokens.color.ink2,
                fontSize: 12,
                opacity: disabled ? 0.5 : 1,
                '&:hover': { borderColor: tokens.color.accent },
            }}
        >
            {src && failed ? (
                name
                    .replace(/[^0-9a-z]/gi, '')
                    .substring(0, 2)
                    .toUpperCase()
            ) : src ? (
                <Box
                    component="img"
                    src={src}
                    alt={name}
                    onError={() => setFailed(true)}
                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
            ) : (
                <>
                    <AddOutlined sx={{ fontSize: 16 }} />
                    <FormattedMessage id="app.common.logo" />
                </>
            )}
        </ButtonBase>
    );

    return (
        <>
            {disabled ? (
                <Tooltip title={<FormattedMessage id="app.contactsAndOrganizations.detail.avatarSaveFirst" />}>
                    <span>{box}</span>
                </Tooltip>
            ) : (
                box
            )}
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                <MenuItem
                    sx={{ fontSize: 13 }}
                    onClick={() => {
                        setAnchor(null);
                        onAdd();
                    }}
                >
                    <FormattedMessage id="app.common.logoChange" />
                </MenuItem>
                <MenuItem
                    sx={{ fontSize: 13, color: tokens.color.crit }}
                    onClick={() => {
                        setAnchor(null);
                        onRemove();
                    }}
                >
                    <FormattedMessage id="app.common.logoRemove" />
                </MenuItem>
            </Menu>
        </>
    );
};
